import React, { useEffect, useRef, useState } from 'react';

export default function CardSwiper({ cards }) {
  const sectionTitle = 'Когда будет полезно';
  const maxSpeedFactor = 6;
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const rafRef = useRef(0);
  const lastFrameTimeRef = useRef(0);
  const progressRef = useRef(0);
  const loopDistanceRef = useRef(0);
  const speedFactorRef = useRef(1);

  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') return window.innerWidth < 640;
    return false;
  });
  const [cardWidth, setCardWidth] = useState(0);
  const [cardHeight, setCardHeight] = useState(0);
  const [trackGap, setTrackGap] = useState(32);

  useEffect(() => {
    const designViewportWidth = 1920;
    const maxScaleViewportWidth = 2560;
    const designContainerWidth = 1464;
    const designGap = 32;
    const designCardWidth = (designContainerWidth - designGap * 2) / 3;
    const designCardHeight = 561;
    const visibleCards = 3;
    const desktopSpeed = 54;

    const applyProgress = () => {
      if (!trackRef.current) return;
      trackRef.current.style.transform = `translate3d(-${progressRef.current / 16}rem, 0, 0)`;
    };

    const animate = (timestamp) => {
      if (!trackRef.current || isMobile || loopDistanceRef.current <= 0) {
        rafRef.current = 0;
        lastFrameTimeRef.current = 0;
        return;
      }

      if (lastFrameTimeRef.current === 0) {
        lastFrameTimeRef.current = timestamp;
      }

      const deltaSeconds = Math.min((timestamp - lastFrameTimeRef.current) / 1000, 0.05);
      lastFrameTimeRef.current = timestamp;

      progressRef.current += desktopSpeed * speedFactorRef.current * deltaSeconds;
      if (progressRef.current >= loopDistanceRef.current) {
        progressRef.current %= loopDistanceRef.current;
      }
      applyProgress();

      rafRef.current = requestAnimationFrame(animate);
    };

    const startAnimation = () => {
      if (rafRef.current || isMobile || loopDistanceRef.current <= 0) return;
      lastFrameTimeRef.current = 0;
      rafRef.current = requestAnimationFrame(animate);
    };

    const stopAnimation = () => {
      if (!rafRef.current) return;
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
      lastFrameTimeRef.current = 0;
    };

    const measure = () => {
      const mobile = window.innerWidth < 640;
      setIsMobile(mobile);

      if (mobile) {
        loopDistanceRef.current = 0;
        progressRef.current = 0;
        speedFactorRef.current = 1;
        setCardWidth(0);
        setCardHeight(0);
        setTrackGap(32);
        if (trackRef.current) {
          trackRef.current.style.width = 'auto';
          trackRef.current.style.transform = 'translate3d(0rem, 0, 0)';
        }
        stopAnimation();
        return;
      }

      if (!viewportRef.current || !trackRef.current) {
        stopAnimation();
        return;
      }

      const viewportWidth = viewportRef.current.clientWidth || 0;
      const cardCount = cards?.length || 0;
      if (!viewportWidth || !cardCount) return;

      const viewportScale = Math.min(window.innerWidth, maxScaleViewportWidth) / designViewportWidth;
      let nextGap = designGap * viewportScale;
      let nextCardWidth = designCardWidth * viewportScale;
      let nextCardHeight = designCardHeight * viewportScale;

      const requiredWidth = nextCardWidth * visibleCards + nextGap * (visibleCards - 1);
      if (requiredWidth > viewportWidth) {
        const fitScale = viewportWidth / requiredWidth;
        nextGap *= fitScale;
        nextCardWidth *= fitScale;
        nextCardHeight *= fitScale;
      }

      const singleTrackWidth = cardCount * nextCardWidth + (cardCount - 1) * nextGap;
      const loopDistance = singleTrackWidth + nextGap;
      const totalTrackWidth = singleTrackWidth * 2 + nextGap;

      loopDistanceRef.current = loopDistance;
      progressRef.current = progressRef.current % loopDistance;
      setCardWidth(nextCardWidth);
      setCardHeight(nextCardHeight);
      setTrackGap(nextGap);
      trackRef.current.style.width = `${totalTrackWidth / 16}rem`;
      applyProgress();
      stopAnimation();
      startAnimation();
    };

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure);

    return () => {
      stopAnimation();
      window.removeEventListener('resize', measure);
      window.removeEventListener('load', measure);
    };
  }, [cards, isMobile]);

  if (!cards || cards.length === 0) return null;

  if (isMobile) {
    return (
      <section className="relative pt-0 pb-0" style={{ backgroundColor: 'var(--card-swiper-bg)' }}>
        <div className="layout-12 mx-auto home-card-swiper-title-wrap">
          <h3 className="home-card-swiper-title flex items-center justify-center font-bold font-tt-norms" style={{ color: 'var(--card-swiper-title)' }}>
            {sectionTitle}
          </h3>
        </div>
        <div className="layout-12 mx-auto home-card-swiper-mobile-list flex flex-col items-center">
          {cards.map((card) => (
            <div
              key={card.id}
              className="home-card-swiper-mobile-card flex flex-col items-center border-2 shadow-sm"
              style={{ borderColor: 'var(--card-swiper-card-border)', backgroundColor: 'var(--card-swiper-card-bg)' }}
            >
              <div className="home-card-swiper-mobile-image-wrap flex w-full items-center justify-center">
                <img
                  src={card.img}
                  alt={card.text}
                  className="home-card-swiper-mobile-image pointer-events-none w-full object-contain"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <p className="home-card-swiper-mobile-text m-0 w-full whitespace-pre-line text-left font-tt-norms font-medium" style={{ color: 'var(--card-swiper-text)' }}>
                {card.text}
              </p>
            </div>
          ))}
        </div>
      </section>
    );
  }

  const loopCards = [...cards, ...cards];

  return (
    <section className="home-card-swiper-section relative pt-0 pb-4 md:pt-0 md:pb-0" style={{ backgroundColor: 'var(--card-swiper-bg)' }}>
      <div className="flex flex-col">
        <div className="layout-12 mx-auto home-title-gap">
          <h3 className="section-title-main">
            {sectionTitle}
          </h3>
        </div>

        <div
          ref={viewportRef}
          className="home-card-swiper-viewport overflow-hidden"
          onMouseMove={(event) => {
            const rect = event.currentTarget.getBoundingClientRect();
            if (!rect.width) return;

            const relativeX = Math.max(0, Math.min(event.clientX - rect.left, rect.width));
            const ratio = relativeX / rect.width;
            speedFactorRef.current = ratio * maxSpeedFactor;
          }}
          onMouseLeave={() => {
            speedFactorRef.current = 1;
          }}
        >
          <div
            ref={trackRef}
            className="flex flex-nowrap items-stretch will-change-transform"
            style={{ transform: 'translate3d(0rem, 0, 0)', gap: `${trackGap / 16}rem` }}
          >
            {loopCards.map((card, index) => (
              <div
                key={`${card.id}-${index}`}
                style={{
                  width: cardWidth ? `${cardWidth / 16}rem` : 'calc((100% - 4rem) / 3)',
                  height: cardHeight ? `${cardHeight / 16}rem` : 'auto',
                  borderColor: 'var(--card-swiper-card-border)',
                  backgroundColor: 'var(--card-swiper-card-bg)'
                }}
                className="flex-shrink-0 rounded-2xl border-2 p-6 shadow-sm transition-shadow hover:shadow-md"
              >
                <div className="flex h-full flex-col">
                  <div className="flex flex-1 items-center justify-center">
                    <img
                      src={card.img}
                      alt={card.text}
                      className="pointer-events-none max-h-[100%] w-full object-contain"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <p className="home-card-swiper-desktop-text w-full whitespace-pre-line font-tt-norms font-medium" style={{ color: 'var(--card-swiper-text)' }}>
                    {card.text}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
